import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Empty,
  Flex,
  Input,
  Switch,
  Tooltip,
  Typography
} from 'antd'
import { Boxes } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { WorkspaceAgent } from 'src/api/facade/types'
import {
  ContentSpinner,
  MonoValue,
  QueryErrorAlert,
  SectionLabel
} from 'src/components'
import type {
  AgentToolWrite,
  SquadToolCatalogEntry,
  SquadToolProvider
} from 'src/services/agentTools'
import { useShowApiErrors } from 'src/utils'

import {
  useAgentTools,
  useSetAgentTools,
  useToolCatalog
} from './useAgentTools'

const { Text } = Typography

/** A provider tool requires a key when its config schema declares `api_key`. */
const hasApiKeyField = (entry: SquadToolCatalogEntry): boolean =>
  Boolean(
    (entry.config_schema?.properties as Record<string, unknown> | undefined)
      ?.api_key
  )

type KeyEditorProps = {
  providerName: string
  /** The currently-stored `config.api_key` for this tool ('' if none). */
  savedKey: string
  readOnly: boolean
  saving: boolean
  onSave: (key: string) => void
}

/**
 * Inline API-key field shown under a BYOK provider tool once it's enabled. The
 * key is part of the tool's `config` (`config.api_key`); a Save button appears
 * only when the draft differs from the stored value. Mirrors the WEB tools
 * picker — the runtime carries no fallback key, so the tool can't run without it.
 */
const ProviderToolKeyEditor = ({
  providerName,
  savedKey,
  readOnly,
  saving,
  onSave
}: KeyEditorProps) => {
  const { t } = useTranslation()

  // Draft re-synced when the stored key changes (after a save / refetch) — the
  // same derived-state-reset pattern as ConnectionScopeEditor.
  const [state, setState] = useState<{ saved: string; draft: string }>(() => ({
    saved: savedKey,
    draft: savedKey
  }))
  if (state.saved !== savedKey) setState({ saved: savedKey, draft: savedKey })
  const { draft } = state
  const dirty = draft.trim() !== savedKey.trim()

  return (
    <div style={{ padding: '0 12px 12px 12px' }}>
      <Text style={{ fontSize: 12, fontWeight: 500 }}>
        {t('workspace.agents.builtinTools.apiKeyLabel', {
          provider: providerName
        })}
      </Text>
      <Flex gap={8} align="flex-start" style={{ marginTop: 6 }}>
        <Input.Password
          value={draft}
          onChange={(e) => setState((s) => ({ ...s, draft: e.target.value }))}
          placeholder={t('workspace.agents.builtinTools.apiKeyPlaceholder')}
          autoComplete="off"
          disabled={readOnly || saving}
          style={{ maxWidth: 360 }}
        />
        {dirty ? (
          <Button
            size="small"
            type="primary"
            disabled={readOnly}
            loading={saving}
            onClick={() => onSave(draft.trim())}
          >
            {t('workspace.agents.access.save')}
          </Button>
        ) : null}
      </Flex>
      <Text
        type={savedKey.trim() ? 'secondary' : 'warning'}
        style={{ fontSize: 12, display: 'block', marginTop: 4 }}
      >
        {savedKey.trim()
          ? t('workspace.agents.builtinTools.apiKeyHelp')
          : t('workspace.agents.builtinTools.apiKeyMissing')}
      </Text>
    </div>
  )
}

type Props = {
  workspaceId: number
  agent: WorkspaceAgent
}

/**
 * External tools tab — built-in tools backed by a third-party provider (e.g.
 * Firecrawl). Same `/api/tools/` catalog + per-agent `/api/agents/{id}/tools/`
 * set as the Connections tab, filtered to entries that carry a `provider`.
 */
export const AgentExternalToolsTab = ({ workspaceId, agent }: Props) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const showApiErrors = useShowApiErrors()
  const readOnly = agent.canEdit === false

  const catalog = useToolCatalog(workspaceId)
  const agentTools = useAgentTools(workspaceId, agent.id)
  const setMut = useSetAgentTools(workspaceId, agent.id)

  const isLoading = catalog.isLoading || agentTools.isLoading
  const isError = catalog.isError || agentTools.isError

  const enabled = new Map(
    (agentTools.data ?? []).map((tool) => [tool.tool_key, tool.config])
  )

  const toggle = (key: string, on: boolean) => {
    const next: AgentToolWrite[] = (agentTools.data ?? [])
      .filter((tool) => tool.tool_key !== key)
      .map((tool) => ({ tool_key: tool.tool_key, config: tool.config }))
    if (on) next.push({ tool_key: key, config: enabled.get(key) ?? {} })

    setMut.mutate(next, {
      onSuccess: () =>
        message.success(t('workspace.agents.builtinTools.updated')),
      onError: (e) => showApiErrors(e)
    })
  }

  // Persist a config patch for an already-enabled tool (e.g. its `api_key`),
  // keeping every other tool's row untouched in the replace-all write.
  const saveConfig = (key: string, patch: Record<string, unknown>) => {
    const next: AgentToolWrite[] = (agentTools.data ?? []).map((tool) =>
      tool.tool_key === key
        ? { tool_key: tool.tool_key, config: { ...tool.config, ...patch } }
        : { tool_key: tool.tool_key, config: tool.config }
    )

    setMut.mutate(next, {
      onSuccess: () =>
        message.success(t('workspace.agents.builtinTools.apiKeySaved')),
      onError: (e) => showApiErrors(e)
    })
  }

  // Provider-backed tools only, grouped by provider, preserving catalog order.
  const groups: {
    provider: SquadToolProvider
    tools: SquadToolCatalogEntry[]
  }[] = []
  for (const entry of catalog.data ?? []) {
    if (!entry.provider) continue
    const group = groups.find((g) => g.provider.slug === entry.provider!.slug)
    if (group) group.tools.push(entry)
    else groups.push({ provider: entry.provider, tools: [entry] })
  }

  const renderTool = (tool: SquadToolCatalogEntry, providerName: string) => {
    const isOn = enabled.has(tool.key)
    const config = enabled.get(tool.key)
    const savedKey =
      typeof config?.api_key === 'string' ? (config.api_key as string) : ''

    return (
      <div
        key={tool.key}
        style={{ borderBottom: '1px solid var(--ant-color-fill-tertiary)' }}
      >
        <Flex
          align="flex-start"
          justify="space-between"
          gap={12}
          style={{ padding: '10px 12px' }}
        >
          <Flex vertical style={{ minWidth: 0 }} gap={2}>
            <Flex align="center" gap={6} wrap="wrap">
              <Text strong style={{ fontSize: 13 }}>
                {tool.name}
              </Text>
              <MonoValue copyable={false} fontSize={11}>
                {tool.key}
              </MonoValue>
            </Flex>
            {tool.description ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {tool.description}
              </Text>
            ) : null}
          </Flex>
          <Tooltip
            title={
              isOn
                ? t('workspace.agents.builtinTools.enabledTip')
                : t('workspace.agents.builtinTools.disabledTip')
            }
          >
            <Switch
              size="small"
              checked={isOn}
              disabled={readOnly || setMut.isPending}
              onChange={(v) => toggle(tool.key, v)}
              style={{ flex: '0 0 auto' }}
            />
          </Tooltip>
        </Flex>
        {isOn && hasApiKeyField(tool) ? (
          <ProviderToolKeyEditor
            providerName={providerName}
            savedKey={savedKey}
            readOnly={readOnly}
            saving={setMut.isPending}
            onSave={(key) => saveConfig(tool.key, { api_key: key })}
          />
        ) : null}
      </div>
    )
  }

  return (
    <Flex vertical gap={14}>
      <Alert
        type="info"
        showIcon
        message={t('workspace.agents.builtinTools.externalIntroTitle')}
        description={t('workspace.agents.builtinTools.externalIntroBody')}
      />

      <Card
        title={t('workspace.agents.builtinTools.externalTitle')}
        styles={{ body: { padding: 0 } }}
      >
        {isError ? (
          <div style={{ padding: 14 }}>
            <QueryErrorAlert
              onRetry={() => {
                void catalog.refetch()
                void agentTools.refetch()
              }}
            />
          </div>
        ) : isLoading ? (
          <ContentSpinner minHeight={160} />
        ) : groups.length === 0 ? (
          <div style={{ padding: 24 }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('workspace.agents.builtinTools.externalEmpty')}
                </Text>
              }
            />
          </div>
        ) : (
          <div>
            {groups.map((group) => (
              <div key={group.provider.slug}>
                <Flex
                  align="center"
                  gap={8}
                  style={{
                    padding: '8px 12px',
                    background: 'var(--ant-color-fill-tertiary)',
                    borderBottom: '1px solid var(--ant-color-border-secondary)'
                  }}
                >
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: '0 0 auto',
                      color: 'var(--ant-color-text-secondary)'
                    }}
                  >
                    {group.provider.icon_url ? (
                      <img
                        src={group.provider.icon_url}
                        alt=""
                        width={16}
                        height={16}
                        style={{ borderRadius: 3 }}
                      />
                    ) : (
                      <Boxes size={14} />
                    )}
                  </span>
                  <SectionLabel>{group.provider.name}</SectionLabel>
                </Flex>
                {group.tools.map((tool) =>
                  renderTool(tool, group.provider.name)
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </Flex>
  )
}
